import { useState } from "react";
import { RegisterLogin } from "../../components/registerLogin/RegisterLogin";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AssistantCemade from "../../components/assistantCemade/AssistantCemade";
import { Capacitor } from "@capacitor/core";

export default function LandingPage() {
  const [showRegisterLogin, setShowRegisterLogin] = useState(false);
  const [isShowFormLogin, setIsShowFormLogin] = useState(true);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* === Helmet for SEO === */}
      <Helmet>
        <title>Cemade - Échange tes talents, pas ton argent</title>
        <meta
          name="description"
          content="Cemade est une plateforme d’échange de services entre voyageurs et résidents. Rejoins-nous pour partager tes talents sans argent."
        />
        <meta property="og:title" content="Cemade - Échange tes talents, pas ton argent" />
        <meta property="og:description" content="Rejoins une communauté solidaire où les services remplacent l'argent. Voyageurs et résidents, échangez ce que vous savez faire." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cemade.fr" />
        <meta property="og:image" content="https://cemade.fr/logo-cemade.png" />
        <meta property="og:image:alt" content="Logo de Cemade - plateforme d’échange de services" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* === Main content === */}
      {
        showRegisterLogin ?
          (
            <RegisterLogin setShowRegisterLogin={setShowRegisterLogin} isShowFormLogin={isShowFormLogin} setIsShowFormLogin={setIsShowFormLogin} />
          )
          :
          (
            <div className="font-sans text-gray-800">
              {/* Navbar */}
              <header className="flex justify-between items-center px-4 py-2 md:px-6 md:py-2 bg-white shadow sticky top-0 z-50">
                <div className="text-lg md:text-xl font-bold cursor-pointer text-[#540B0E]" onClick={() => scrollToSection('hero')}>
                  <img src='logo-cemade.png' className="h-12 sm:h-20 md:h-28 lg:h-32 w-auto mr-3" alt="Logo cemade" />
                </div>
                <div className="flex items-center gap-3 space-y-2 md:space-y-0 md:space-x-4 text-sm md:text-base">
                  <Link id="btn-ser-need-available" to="/terms-services#section-cible" className="hidden md:inline hover:text-[#335C67]"> Services et besoins disponibles</Link>
                  <button type="button" id="functioning" className="hidden md:inline hover:text-[#335C67]" onClick={() => scrollToSection('fonctionnement')}>Fonctionnement</button>
                  <button type="button" className="hover:text-[#335C67] whitespace-nowrap"
                    onClick={() => {
                      setShowRegisterLogin(true);
                      setIsShowFormLogin(true);
                    }}
                  >
                    Se connecter
                  </button>
                  <button

                    className="bg-[#335C67] px-1 sm:px-3 md:px-4 py-2 text-white hover:bg-[#E09F3E] rounded-xl whitespace-nowrap"
                    onClick={() => {
                      setShowRegisterLogin(true);
                      setIsShowFormLogin(false);
                    }}
                  >
                    Créer un compte
                  </button>
                  {/* logo instagram */}
                  <span>
                    <a
                      href="https://www.instagram.com/cemade.troc"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Cemade sur Instagram"
                      className="inline-flex items-center space-x-2 hover:opacity-80 transition"
                    >
                      <img
                        src="/logo-instagram.svg"
                        alt="Suivez Cemade sur Instagram"
                        className="w-8 h-8"
                      />
                      <span className="sr-only">Cemade sur Instagram</span>
                    </a>
                  </span>
                  {/* logo play store */}
                  {
                    !Capacitor.isNativePlatform() &&
                    <span>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.cemade.troc&pcampaignid=web_share"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Application mobile Cemade disponible sur Play Store"
                        className="inline-flex items-center space-x-2 hover:opacity-80 transition"
                      >
                        <img
                          src="/logo-playstore.svg"
                          alt="logo play store cliquable pour télécharger l'application mobile Cemade"
                          className="w-8 h-8"
                        />
                        <span className="sr-only">Application mobile Cemade disponible sur Play Store</span>
                      </a>
                    </span>
                  }
                </div>
              </header>

              {/* Hero Section */}
              <section id="hero" className="text-center py-16 md:py-20 px-4 bg-[#FFF3B0]">
                <h1 className="text-3xl md:text-5xl font-extrabold mb-4 text-[#540B0E]">Échange tes talents, pas ton argent.</h1>
                <p className="text-base md:text-lg mb-6 text-[#335C67]">
                  <strong>Cemade</strong> est une plateforme d’échange de services entre voyageurs et résidents en France.<br />
                  Que tu sois nomade en van, randonneur ou sédentaire en quête de coups de main, crée ton profil gratuitement, propose tes compétences
                  et trouve des services utiles autour de toi.<br />
                  <strong>Le troc de services, réinventé.</strong>
                </p>
                <button
                  className="bg-[#E09F3E] text-white px-5 md:px-6 py-3 rounded-xl text-base md:text-lg hover:bg-[#335C67]"
                  onClick={() => {
                    scrollToSection('fonctionnement');
                    setShowRegisterLogin(true);
                    setIsShowFormLogin(false);
                  }}
                >
                  Commencer maintenant
                </button>
              </section>

              {/* Cemade IA Assistant - chat flottant */}
              <AssistantCemade />

              {/* trav and res card section */}
              <NomadeResidentPitch />

              {/* How it works */}
              <section className="py-12 md:py-16 px-4 md:px-20 text-center" id="fonctionnement">
                <h2 className="text-2xl md:text-3xl font-bold mb-10 text-[#540B0E]">Comment fonctionne le troc sur Cemade ?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                  <div className="flex flex-col items-center">
                    <h3 className="text-lg md:text-xl font-semibold mb-2 text-[#335C67]">1. Crée ton profil</h3>
                    <p className="text-sm md:text-base">Présente toi simplement</p>
                    <p className="text-sm md:text-base">Ajoute tes besoins et les services que tu proposes.</p>

                  </div>
                  <div className="flex flex-col items-center">
                    <h3 className="text-lg md:text-xl font-semibold mb-2 text-[#335C67]">2. Trouve des trocs</h3>
                    <p className="text-sm md:text-base">Parcours les offres des résidents ou nomades autour de toi.</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <h3 className="text-lg md:text-xl font-semibold mb-2 text-[#335C67]">3. Échange équitable</h3>
                    <p className="text-sm md:text-base">Lance une discussion pour organiser des échanges de services gagnant-gagnant.</p>
                  </div>
                </div>
              </section>

              {/* Concrete examples */}
              <section id="concrete-examples" className="py-8 bg-gray-50 rounded-xl shadow-inner w-full max-w-5xl mx-auto px-4 sm:px-8 my-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-greenCustom">
                  Des cas concrets qui parlent à tous
                </h2>
                <div className="flex flex-col gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <p className="text-center text-gray-700 italic">
                      <strong>Un voyageur</strong> propose de <strong>surveiller la maison d'un résident</strong> ou de <strong>créer une présence sur la parcelle d'un agriculteur</strong>. <br />
                      Celui-ci l'aide en lui offrant en échange <strong>un lieu sécurisé où stationner</strong>.
                    </p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <p className="text-center text-gray-700 italic">
                      <strong>Un résident</strong> héberge <strong>un cycliste de passage</strong>. <br />
                      Celui-ci l'aide en <strong>taillant ses haies</strong> ou en rendant un petit service de jardinage.
                    </p>
                  </div>
                </div>
              </section>

              {/*Freemium and Premium Section */}
              <section className="bg-gray-100 py-12 md:py-16 px-4 md:px-20 text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-10 text-[#540B0E]"><strong>Forfait Basic ou Premium avec Cemade</strong></h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Carte Basic */}
                  <div className="bg-white p-6 md:p-8 rounded-2xl shadow cursor-default">
                    <h3 className="text-xl md:text-2xl font-semibold mb-4 text-[#335C67]">Basic - gratuit</h3>
                    <ul className="space-y-2 text-sm md:text-base">
                      <li>✅ Proposez au moins un service et ajoutez au moins un besoin</li>
                      <li>✅ Réalise un troc par mois</li>
                      <li>✅ Tchat instantanée</li>
                    </ul>
                  </div>

                  {/* Carte Premium */}
                  <div className="bg-[#335C67] text-white p-6 md:p-8 rounded-2xl shadow cursor-default">
                    <h3 className="flex flex-col items-center text-xl md:text-2xl font-semibold mb-4">Premium - 2,99 €</h3>
                    <ul className="space-y-2 text-sm md:text-base">
                      <li>✅ Inclut les fonctionnalités du forfait Basic</li>
                      <li>🚀 Jusqu’à 8 trocs par mois</li>
                      <li>✅ Recherche possible dans toute la France (pour les voyageurs)</li>
                    </ul>
                  </div>
                </div>
              </section>


              {/* Share app section */}
              <section className="bg-white py-12 px-4 md:px-20 text-center text-[#335C67]">
                <div className="max-w-3xl mx-auto bg-[#FFF3B0] border-l-4 border-[#E09F3E] p-6 rounded-xl shadow-md">
                  <h2 className="text-xl md:text-2xl font-semibold mb-4 text-[#540B0E]">💛 Cemade c’est aussi une communauté qui ne tient que par vous.</h2>
                  <p className="text-base md:text-lg">
                    Chaque échange est une graine. Chaque partage est une lumière. Chaque mot est un pont. <br />
                    <strong>Elle vous appartient. Elle vit par vous. Elle prospérera avec vous.</strong>
                  </p>
                </div>
              </section>

              {/* Call to Action */}
              <section className="py-12 md:py-16 text-center bg-[#FFF3B0]">
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#540B0E]">Prêt à rejoindre la communauté ?</h2>
                <button
                  className="bg-[#E09F3E] text-white px-5 md:px-6 py-3 rounded-xl text-base md:text-lg  hover:bg-[#335C67]"
                  onClick={() => {
                    setShowRegisterLogin(true);
                    setIsShowFormLogin(false);
                  }}
                >
                  Je m'inscris maintenant
                </button>
              </section>

              <section className="text-center px-4 py-8 text-sm text-gray-700">
                <p>
                  Cemade est une plateforme collaborative qui favorise le lien social à travers l’échange de services entre habitants et voyageurs.
                  Grâce à un système de troc équitable et une interface intuitive, tu peux proposer ou recevoir de l’aide en toute simplicité.
                </p>
              </section>

              <section id="section-follow-us" className="text-center py-6">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Suivez-nous sur
                </p>
                <a
                  href="https://www.instagram.com/cemade.troc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 hover:opacity-80 transition"
                >
                  <img
                    src="/logo-instagram.svg"
                    alt="Logo Instagram"
                    className="w-8 h-8"
                  />
                  <span className="text-base font-medium text-gray-700">@cemade.troc</span>
                </a>
              </section>
            </div>
          )
      }
    </>
  );
}

function NomadeResidentPitch() {
  return (
    <section className="bg-white py-16 px-4 md:px-20 text-center text-[#335C67]">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#540B0E]">Une plateforme pensée pour les nomades et les résidents</h2>
      <div className="grid md:grid-cols-2 gap-10">
        {/* Nomade */}
        <div className="bg-[#FFF3B0] p-6 rounded-2xl shadow border-[#E09F3E] ">
          <div className="flex">
            <img src="trav6.png" alt="image d'un fourgon repésentant les nomades" className="max-w-full h-auto" />
          </div>
          <h3 className="text-2xl font-semibold mb-2">Tu es nomade ?</h3>
          <p className="text-base md:text-lg">
            En déplacement, tu peux rapidement trouver des services locaux (hébergement, aide, activités) en échange de tes compétences. Cemade t'accompagne partout.
          </p>
        </div>

        {/* Résident */}
        <div className="bg-[#FFF3B0] p-6 rounded-2xl shadow border-[#E09F3E]">
          <div className="flex justify-center">
            <img src="res1.png" alt="image d'une maisonette repésentant les résidents" className="max-w-full h-auto" />
          </div>
          <h3 className="text-2xl font-semibold mb-2">Tu es résident ?</h3>
          <p className="text-base md:text-lg">
            Propose tes services aux voyageurs de passage et échange contre d'autres services utiles pour toi ou tes proches. Valorise ton temps au cœur de ta communauté.
          </p>
        </div>
      </div>

      <div className="mt-12">
        <p className="text-lg font-medium text-[#540B0E]">Cemade crée du lien entre ceux qui voyagent et ceux qui accueillent. Gratuitement ou en version premium, chacun y trouve sa place.</p>
      </div>
    </section>
  );
}

// 💛 TrocApp appartient à celles et ceux qui l’utilisent.
// Son succès repose sur le partage, les recommandations, et les échanges que vous y créez.
// Chaque service rendu, chaque contact établi, chaque mot partagé compte.
// Fais vivre la communauté : elle est entre tes mains.

// 🔁 TrocApp, c’est votre app.
// Son succès dépend de vous. Partagez-la, vivez-la, faites-la grandir 🌱
// #Communauté #Échange #TrocResponsable

// 🌱 TrocApp, c’est vous.
// Une appli ne vit que si ses utilisateurs la font vibrer.
// Partage-la. Parle-en. Utilise-la.
// Construisons-la ensemble.
// #TrocResponsable #ForceDuCollectif #TrocApp

/* <li>Switch de rôle</li>
<li>💬 Messagerie directe</li> 
<li>🛡️ Évaluations de fiabilité</li> */
